import { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { urls } from "../../../utils/urls";
import { useAuth } from "../../../context_providers/auth/authProvider";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { ACTION, MODULE } from "../../../utils/enums";
import LoadingWidget from "../../../context_providers/modal/loader";
import { fetchGet, fetchPut } from "../../../service/api_client";
import ModalDialog from "../../../ui/new_components/common/modal";
import {
  Project,
  ProjectApprovalStatus,
  ProjectCloseStatus,
  projectDefaultValue,
} from "../models/project_model";
import ProjectApprovalBottomSheet from "./components/projectApprovalBottomSheet";
import CostCenterSearchField from "../cost_center/components/costCenterSearch";
import "../../../styles/responsive_shrink.css";
import { Clock } from "react-feather";
import EntityHistory, {
  GetMasterLogsByEntityApi,
} from "../../purchase_order/purchase_order_create/components/entity_logs";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../ui/new_components/common/drawer_modal";

interface ProjectViewScreenProps {
  data?: Project;
  onClose: (id: Project) => void;
}

const ProjectViewScreen: FunctionComponent<ProjectViewScreenProps> = (
  props,
) => {
  const { id } = useParams<{ id: string }>();
  const navaigation = useHistory();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/project`;
  const is_view_page = useRouteMatch(
    urls.procurement_project + "/view-project/:id",
  );
  const [view, setView] = useState(false);
  const [maker, setMaker] = useState(false);
  const [close, setClose] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { isAuthorised } = useAuth();
  const { showToast } = useToast();
  const [data, setData] = useState<Project>({
    ...(props.data || { ...projectDefaultValue }),
  });

  useEffect(() => {
    if (id && is_view_page) getData(id);
  }, [id]);

  useEffect(() => {
    const maker_permission_result = isAuthorised({
      module: MODULE.project_master,
      action: ACTION.CREATE,
    });
    const checker_permission_result = isAuthorised({
      module: MODULE.project_master_maker,
      action: ACTION.READ,
    });
    setView(checker_permission_result);
    setMaker(maker_permission_result);
  }, [isAuthorised]); //

  // --- Phase 3: project approval submit handler. Only fires for new-flow projects
  // (those with data.approval_level set). Hits the new /approval endpoint.
  const submitApproval = async (payload: {
    approval_status: string;
    level: string;
    modified_amount?: number;
    reason?: string;
  }) => {
    setLoading(true);
    try {
      const res = await fetchPut(`${url}/${data.id}/approval`, {
        ...payload,
        id: data.id,
      });
      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        setData((prev) => ({ ...prev, ...res.data }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  const ProjectClosedStatus = async (status: string) => {
    try {
      await fetchPut(`${url}/${data.id}/close`, data);
      showToast({
        type: ToastType.success,
        text: `Project Status: ${status}`,
      });
      navaigation.push(urls.procurement_project);
    } catch (error) {
      console.error("Error while updating project status:", error);
      showToast({
        type: ToastType.error,
        text: "Failed to update status. Try again later.",
      });
    }
  };

  const getData = async (id: string) => {
    setLoading(true);
    const res = await fetchGet(url + "/" + id);
    if (res.success) {
      setData(res.data);
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  useEffect(() => {
    const total = data.budget + (data.addAdditionalBudget || 0);
    setData((prevData) => ({
      ...prevData,
      totalBudget: total,
    }));
  }, [data.budget, data.addAdditionalBudget]);

  return (
    <div className="mobile-shrink">
      {/* --- Phase 3: approval bottom sheet renders for new-flow projects only.
          The check is the presence of approval_level (set at create time when
          the parent CC has a business_unit_id). Old-flow projects skip this. */}
      {data.approval_level && (
        <div className="mx-5 mt-3">
          <div className="text-sm font-semibold mb-1">
            Status:{" "}
            <span
              className={
                "px-2 py-0.5 rounded text-xs " +
                (data.approvalStatus === ProjectApprovalStatus.approved
                  ? "bg-green-100 text-green-700"
                  : data.approvalStatus === ProjectApprovalStatus.rejected
                  ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-700")
              }
            >
              {data.approvalStatus || "—"}
            </span>
          </div>
          <ProjectApprovalBottomSheet data={data} onSubmit={submitApproval} />
        </div>
      )}

      {close && (
        <ModalDialog
          show={close}
          title={"Project Status: Closed"}
          onClose={() => {
            setClose(false);
            setData({
              ...data,
              projectStatus: ProjectCloseStatus.open,
            });
          }}
        >
          <div className="bg-white p-6 rounded-md shadow-lg w-full md:w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to close the project?
            </h2>

            <div className="flex justify-between mt-4">
              <button
                id="modal_no_btn"
                onClick={() => {
                  setClose(false);
                  setData({
                    ...data,
                    projectStatus: ProjectCloseStatus.open,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                id="modal_yes_btn"
                onClick={() => {
                  ProjectClosedStatus(ProjectCloseStatus.closed);
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      <div className="grid grid-cols-1 m-2 md:m-5 gap-2 select-none text-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
            Project View: {data.id}
          </h1>

          {data?.id && (
            <button
              id="history_btn"
              onClick={() => setShowHistory(true)}
              className="flex items-center gap-1 text-sm hover:text-gray-600  text-myPrimaryColor"
              title="View History"
            >
              <Clock size={32} />
            </button>
          )}
        </div>
        <div className="flex justify-end   text-sm items-center gap-1 ">
          {" "}
          <div className="bg-white rounded w-52 flex gap-1"></div>
        </div>
      </div>
      <div className="m-2 md:m-5">
        <div className="p-3 md:p-5 flex flex-col gap-5 w-full shadow bg-white rounded-lg">
          <div className="flex flex-col md:flex-row gap-5 w-full md:w-1/2">
            <div className="flex flex-col w-full">
              <div className="flex flex-col items-start text-sm">
                <label className="font-semibold w-full" htmlFor="name">
                  Project Name*
                </label>
                <input
                  placeholder="Enter Name"
                  className="p-1 rounded focus:outline-none w-full border"
                  id="name"
                  name="name"
                  value={data.name || ""}
                  required
                  type="text"
                  readOnly
                />
              </div>

              <div className="flex flex-col items-start text-sm my-2 w-full">
                <label className="font-semibold w-full" htmlFor="desc">
                  Description
                </label>
                <textarea
                  placeholder="Short description"
                  className="p-1 rounded focus:outline-none w-full border"
                  id="description"
                  name="description"
                  value={data.description || ""}
                  readOnly
                ></textarea>
              </div>
              <div className="flex flex-col items-start text-sm">
                <label className="font-semibold w-full" htmlFor="name">
                  Batch Name
                </label>
                <input
                  placeholder=""
                  className="p-1 rounded focus:outline-none w-full border"
                  id="id"
                  name="id"
                  value={data.batchName || ""}
                  required
                  type="text"
                  readOnly
                />
              </div>
            </div>
            <div className="flex flex-col w-full">
              <div className="flex flex-col items-start text-sm">
                <label className="font-semibold w-full" htmlFor="name">
                  Cost Center*
                </label>
                <CostCenterSearchField
                  disabled={true}
                  placeholder="cost center"
                  value={data.cost_center_category}
                  onSelect={(d) => {
                    setData((prevData) => ({
                      ...prevData,
                      cost_center_category: d.name,
                      cost_center_id: d.id,
                    }));
                  }}
                />
              </div>

              <div className="flex flex-col items-start text-sm my-2">
                <label className="font-semibold w-full" htmlFor="name">
                  Cost Center ID*
                </label>
                <input
                  placeholder=""
                  className="p-1 rounded focus:outline-none w-full border"
                  id="id"
                  name="id"
                  value={data.cost_center_id || ""}
                  required
                  type="text"
                  readOnly
                />
              </div>

              <div className="flex flex-col items-start text-sm my-2">
                <label className="font-semibold w-full" htmlFor="name">
                  Batch Size
                </label>
                <input
                  placeholder=""
                  className="p-1 rounded focus:outline-none w-full border"
                  id="id"
                  name="id"
                  value={data.batchSize || ""}
                  required
                  type="number"
                  readOnly
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start text-sm">
            <label className="font-semibold w-full" htmlFor="name">
              Budget*
            </label>
            <input
              placeholder="Enter budget"
              className="p-1 rounded focus:outline-none w-full md:w-1/4 border"
              id="budget"
              name="budget"
              value={data.budget || ""} // Correctly bind the value to data.budget
              required
              type="number"
              readOnly
            />
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            {id && data.totalAdditionalFunds && (
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex flex-col items-start text-sm">
                  <label className="font-semibold w-full" htmlFor="name">
                    Additional Budget*
                  </label>
                  <input
                    placeholder="budget"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="add_budget"
                    name="add_budget"
                    value={data.addAdditionalBudget || ""}
                    required
                    type="number"
                    readOnly
                  />
                  <span className="mt-2">
                    <label className="text-green-500 w-full">
                      Add Amounts:
                    </label>
                    <p className="text-sm">
                      {data.additionalFunds
                        ?.map((fund, index) => fund.fund_amount)
                        .join(", ")}
                    </p>
                  </span>
                </div>
                <div className="flex flex-col items-start text-sm">
                  <label className="font-semibold w-full" htmlFor="last_budget">
                    Last Updated Budget
                  </label>
                  <input
                    disabled
                    placeholder="budget"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="last_budget"
                    name="last_budget"
                    value={data.lastUpdatedBudget || ""}
                    required
                    type="number"
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col items-start text-sm">
            <label className="font-semibold w-full" htmlFor="name">
              Total Budget*
            </label>
            <input
              placeholder="total budget"
              className="p-1 rounded focus:outline-none w-full md:w-1/4 border"
              id="tot_budget"
              name="tot_budget"
              value={data.totalBudget || ""}
              required
              type="number"
              readOnly
            />
          </div>
          <div className="flex flex-col md:flex-row gap-5 w-full">
            <div className="w-full md:w-1/2">
              <div>
                <label className="text-sm font-semibold">
                  Balance is calculated based on the last approved budget.
                </label>
              </div>
              <div className="bg-gray-100 p-5 rounded-md">
                <div className="flex flex-col items-start text-sm">
                  <label className="font-semibold w-full" htmlFor="name">
                    Spent
                  </label>
                  <input
                    placeholder=""
                    className="p-1 rounded focus:outline-none w-full border"
                    id="spent"
                    name="spent"
                    value={data.spent || ""}
                    required
                    type="number"
                    readOnly
                  />
                </div>
                <div className="flex flex-col items-start text-sm">
                  <label className="font-semibold w-full" htmlFor="name">
                    Balance{" "}
                  </label>
                  <input
                    placeholder=""
                    className="p-1 rounded focus:outline-none w-full border"
                    id="balance"
                    name="balance"
                    value={data.lastUpdatedBudget - data.spent || ""}
                    required
                    type="number"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="pb-10 m-2 md:m-5">
        <div className="my-10">
          {data.projectStatus === ProjectCloseStatus.open && (
            <div className="flex justify-end gap-5 mt-5 mb-2">
              {maker && !view && (
                <button
                  id="close_project_btn"
                  onClick={() => {
                    setClose(true);
                    setData({
                      ...data,
                      projectStatus: ProjectCloseStatus.closed,
                    });
                  }}
                  className="bg-myPrimaryColor text-white rounded-lg px-6 py-1.5 text-sm cursor-pointer duration-300"
                >
                  Close Project
                </button>
              )}
              <button
                id="quit_btn"
                onClick={() => {
                  navaigation.push(urls.procurement_project);
                }}
                type="button"
                className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
              >
                Quit
              </button>
            </div>
          )}
        </div>
        {data.projectStatus === ProjectCloseStatus.closed && (
          <div className="flex justify-end gap-5 mt-5 mb-2">
            <button
              id="quit_btn"
              onClick={() => {
                navaigation.push(urls.procurement_project);
              }}
              type="button"
              className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
            >
              Quit
            </button>
          </div>
        )}
      </div>

      {loading && <LoadingWidget />}
      {showHistory && data?.id && (
        <DrawerModal
          location={DrawerModalLoaction.right}
          show={showHistory}
          onClose={() => setShowHistory(false)}
          title="Project History"
        >
          <EntityHistory
            fetchLogs={({ page, count }) =>
              GetMasterLogsByEntityApi({
                entityType: "project",
                entityId: data.id,
                page,
                count,
              })
            }
          />
        </DrawerModal>
      )}
    </div>
  );
};
export default ProjectViewScreen;
